import React from 'react'
import { IconType } from 'react-icons'
import { Icon } from '@chakra-ui/icons'
import { Tooltip } from '@chakra-ui/react'
import { Box, Text, Spacer } from '@chakra-ui/react'
import {
    FaCheck,
    FaCommentDots,
    FaEnvelope,
    FaMessage,
    FaNoteSticky,
} from 'react-icons/fa6'

import SharedStack from '~components/shared/wrappers/shared-stack'
import { MessageMethod } from '~graphql/generated/graphql'
import { SetStateFn } from '~config/constants'
import SharedLabel from '~components/shared/form/shared-label'
import { disabledStyles } from '~utils/constants/disabled-styles'

interface Props {
    messageMethods: MessageMethod[]
    setMessageMethods: SetStateFn<MessageMethod[]>
    smsDisabledMessage: string | undefined | null
    emailDisabledMessage: string | undefined | null
    isInternal: boolean
    setIsInternal?: (val: boolean) => void
}

export default function ChatChannelSelect({
    messageMethods,
    setMessageMethods,
    smsDisabledMessage,
    emailDisabledMessage,
    isInternal,
    setIsInternal,
}: Props) {
    function handleSelectInternal() {
        setIsInternal?.(true)
        setMessageMethods([])
    }

    function handleSelectChat() {
        setIsInternal?.(false)
        setMessageMethods([])
    }

    function handleSelectEmail() {
        setIsInternal?.(false)
        if (messageMethods.includes(MessageMethod.Email)) {
            if (messageMethods.length > 1) {
                setMessageMethods(
                    messageMethods.filter(
                        method => method !== MessageMethod.Email,
                    ),
                )
            }
        } else {
            setMessageMethods([...(messageMethods ?? []), MessageMethod.Email])
        }
    }

    function handleSelectSms() {
        setIsInternal?.(false)
        if (messageMethods.includes(MessageMethod.Sms)) {
            if (messageMethods.length > 1) {
                setMessageMethods(
                    messageMethods.filter(
                        method => method !== MessageMethod.Sms,
                    ),
                )
            }
        } else {
            setMessageMethods([...(messageMethods ?? []), MessageMethod.Sms])
        }
    }

    return (
        <Box>
            {/* <SharedLabel fontSize="xs" mb={1} pl={2} pt={1}>
                Delivery Method
            </SharedLabel> */}
            <SharedStack spacing={0}>
                {setIsInternal && (
                    <ChannelOption
                        icon={FaNoteSticky}
                        label="Internal Note"
                        isSelected={isInternal}
                        onClick={handleSelectInternal}
                        colorScheme="orange"
                    />
                )}
                <ChannelOption
                    icon={FaMessage}
                    label="Chckvet Only"
                    isSelected={!isInternal && messageMethods.length === 0}
                    onClick={handleSelectChat}
                    colorScheme="brand"
                />
                <ChannelOption
                    icon={FaEnvelope}
                    label="Email"
                    isSelected={
                        !isInternal &&
                        messageMethods.includes(MessageMethod.Email)
                    }
                    onClick={handleSelectEmail}
                    colorScheme="brand"
                    disabledMessage={emailDisabledMessage}
                />
                <ChannelOption
                    icon={FaCommentDots}
                    label="SMS"
                    isSelected={
                        !isInternal &&
                        messageMethods.includes(MessageMethod.Sms)
                    }
                    onClick={handleSelectSms}
                    colorScheme="brand"
                    disabledMessage={smsDisabledMessage}
                />
            </SharedStack>
        </Box>
    )
}

interface ChannelOptionProps {
    icon: IconType
    label: string
    isSelected: boolean
    onClick: () => void
    colorScheme: string
    disabledMessage?: string | null
}

function ChannelOption({
    icon,
    label,
    isSelected,
    onClick,
    colorScheme,
    disabledMessage,
}: ChannelOptionProps) {
    return (
        <Tooltip label={disabledMessage} isDisabled={!disabledMessage}>
            <SharedStack
                row
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                rounded="lg"
                p={2}
                onClick={onClick}
                {...disabledStyles(!!disabledMessage)}
            >
                <Icon
                    as={icon}
                    fontSize="sm"
                    color={isSelected ? `${colorScheme}.600` : 'gray.700'}
                />
                <Text
                    fontSize="sm"
                    color={isSelected ? `${colorScheme}.600` : 'gray.700'}
                    fontWeight="medium"
                >
                    {label}
                </Text>
                <Spacer />
                {isSelected && (
                    <Icon
                        as={FaCheck}
                        fontSize="sm"
                        color={`${colorScheme}.600`}
                    />
                )}
            </SharedStack>
        </Tooltip>
    )
}
